import React, {useRef} from 'react';
import {observer} from 'mobx-react';
import {createContext, useEffect} from 'react';
import {useIntl} from 'react-intl';
import useSocket from 'src/hooks/use-socket.js';
import useStore from 'src/hooks/use-store.js';
import {of} from 'rxjs';
import {mergeAll, mergeMap} from 'rxjs/operators';
import AudioPlayer from 'src/infra/AudioPlayer.js';
import {REPORT_TYPE_LABEL} from 'src/helpers/report.js';

export const EventsContext = createContext({});

/**
 * "Relatório <tipo>" no lugar do nome do dispositivo -- reportReady não é sobre o dispositivo
 * âncora (só existe pra satisfazer o deviceId obrigatório do evento), é sobre o relatório.
 */
export const eventDisplayName = (intl, device, event) => {
    if (event.type === 'reportReady') {
        return intl.formatMessage({id: REPORT_TYPE_LABEL[event.attributes?.reportType] || 'sharedReportJobs'});
    }
    return device ? device.name : '';
};

const EventsProvider = ({children}) => {
    const audioPlayer = useRef(AudioPlayer.getInstance()).current;
    const intl = useIntl();
    const {events: eventsSocket} = useSocket();
    const {store} = useStore();
    const {auth, events, devices} = store;

    function onAddEvent() {
        if (events.isSoundEnabled) {
            audioPlayer.play('siren');
        }
    }

    useEffect(() => {
        let sub;

        if (auth.isLogged) {
            sub = eventsSocket
                .pipe(
                    mergeAll(),
                    mergeMap(
                        ({deviceId}) => of(devices.findById(deviceId)),
                        // A data fica como veio do servidor. Converter aqui para o fuso da conta
                        // fazia o mesmo evento aparecer com hora diferente conforme a tela, porque
                        // cada uma formata de um jeito -- e evento buscado pela API nem passava por
                        // esta conversao. Quem desenha formata, no fuso de quem olha.
                        (e, d) => ({
                            name: eventDisplayName(intl, d, e),
                            ...e,
                        }),
                    ),
                )
                .subscribe((event) => {
                    events.add(event);
                    onAddEvent();
                });
        }

        return () => {
            if (sub && sub.unsubscribe) {
                sub.unsubscribe();
            }
        };
    }, [auth.isLogged]);

    return (
        <EventsContext.Provider value={{}}>
            {children}
        </EventsContext.Provider>
    );
};

export default observer(EventsProvider);
